import React, { useState, useMemo, useCallback } from 'react';
import { format } from 'date-fns';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';

interface DateRange {
  from?: string;
  to?: string;
}

interface Props {
  value: DateRange;
  onChange: (dateRange: DateRange) => void;
}

type PresetValue = 'custom' | 'last-day' | 'last-week' | 'last-month' | 'ytd';

const DateRangePicker: React.FC<Props> = ({ value, onChange }) => {
  const [customFromDate, setCustomFromDate] = useState<Date | null>(null);
  const [customToDate, setCustomToDate] = useState<Date | null>(null);
  const [isCustomMode, setIsCustomMode] = useState(false);

  // Generate preset date ranges
  const generatePresetRange = useCallback(
    (presetType: PresetValue): DateRange => {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      switch (presetType) {
        case 'last-day': {
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          return {
            from: yesterday.toISOString().split('T')[0],
            to: today.toISOString().split('T')[0],
          };
        }

        case 'last-week': {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return {
            from: weekAgo.toISOString().split('T')[0],
          };
        }

        case 'last-month': {
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return {
            from: monthAgo.toISOString().split('T')[0],
          };
        }

        case 'ytd': {
          const startOfYear = new Date(now.getFullYear(), 0, 1);
          return {
            from: startOfYear.toISOString().split('T')[0],
          };
        }

        case 'custom':
        default:
          return {
            from: customFromDate?.toISOString().split('T')[0],
            to: customToDate?.toISOString().split('T')[0],
          };
      }
    },
    [customFromDate, customToDate],
  );

  // Determine current preset based on value - computed during render
  const currentPreset = useMemo((): PresetValue => {
    if (isCustomMode) return 'custom';

    if (!value.from && !value.to) return 'last-week';

    const presets: PresetValue[] = [
      'last-day',
      'last-week',
      'last-month',
      'ytd',
    ];

    for (const preset of presets) {
      const range = generatePresetRange(preset);
      if (range.from === value.from && range.to === value.to) {
        return preset;
      }
    }

    return 'custom';
  }, [value, generatePresetRange, isCustomMode]);

  // Handle preset change - all logic in event handler
  const handlePresetChange = (newPreset: string | undefined) => {
    if (!newPreset) return;
    const preset = newPreset as PresetValue;

    if (preset === 'custom') {
      // Switch to custom mode
      setIsCustomMode(true);
      return;
    }

    // Switch out of custom mode and apply preset
    setIsCustomMode(false);
    const newRange = generatePresetRange(preset);
    onChange(newRange);
  };

  // Handle custom date changes - all logic in event handler
  const handleCustomFromDateChange = (date: Date | undefined) => {
    setCustomFromDate(date || null);

    // Only call onChange if both dates are selected
    if (date && customToDate) {
      const newRange = {
        from: date.toISOString().split('T')[0],
        to: customToDate.toISOString().split('T')[0],
      };
      onChange(newRange);
    }
  };

  const handleCustomToDateChange = (date: Date | undefined) => {
    setCustomToDate(date || null);

    // Only call onChange if both dates are selected
    if (date && customFromDate) {
      const newRange = {
        from: customFromDate.toISOString().split('T')[0],
        to: date.toISOString().split('T')[0],
      };
      onChange(newRange);
    }
  };

  const formatPresetLabel = (presetType: PresetValue): string => {
    switch (presetType) {
      case 'last-day':
        return 'Last Day';
      case 'last-week':
        return 'Last Week';
      case 'last-month':
        return 'Last Month';
      case 'ytd':
        return 'Year to Date';
      case 'custom':
        return 'Custom Range';
      default:
        return 'Last Week';
    }
  };

  const formatDateRangePreview = (
    range: DateRange,
    preset: PresetValue,
  ): string => {
    const formatDate = (dateStr: string | undefined): string => {
      if (!dateStr) return 'present';
      return format(new Date(dateStr), 'MMM dd, yyyy');
    };

    const dateText = `${formatDate(range.from)} → ${formatDate(range.to)}`;
    const presetText = formatPresetLabel(preset);
    return `${dateText} (${presetText})`;
  };

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      {/* Single Row - Preset Picker and Custom Date Inputs */}
      <div className="flex items-center gap-6">
        <ToggleGroup
          type="single"
          value={currentPreset}
          onValueChange={handlePresetChange}
          className="inline-flex rounded-lg border bg-muted/40 overflow-hidden"
        >
          <ToggleGroupItem
            value="last-day"
            className={cn(
              'rounded-none px-6 py-2 text-sm font-medium cursor-pointer',
              currentPreset === 'last-day'
                ? 'bg-primary text-primary-foreground shadow-md font-semibold'
                : 'text-muted-foreground hover:bg-background/60',
            )}
          >
            Last Day
          </ToggleGroupItem>
          <ToggleGroupItem
            value="last-week"
            className={cn(
              'rounded-none px-6 py-2 text-sm font-medium border-l border-border cursor-pointer',
              currentPreset === 'last-week'
                ? 'bg-primary text-primary-foreground shadow-md font-semibold'
                : 'text-muted-foreground hover:bg-background/60',
            )}
          >
            Last Week
          </ToggleGroupItem>
          <ToggleGroupItem
            value="last-month"
            className={cn(
              'rounded-none px-6 py-2 text-sm font-medium border-l border-border cursor-pointer',
              currentPreset === 'last-month'
                ? 'bg-primary text-primary-foreground shadow-md font-semibold'
                : 'text-muted-foreground hover:bg-background/60',
            )}
          >
            Last Month
          </ToggleGroupItem>
          <ToggleGroupItem
            value="ytd"
            className={cn(
              'rounded-none px-6 py-2 text-sm font-medium border-l border-border cursor-pointer',
              currentPreset === 'ytd'
                ? 'bg-primary text-primary-foreground shadow-md font-semibold'
                : 'text-muted-foreground hover:bg-background/60',
            )}
          >
            YTD
          </ToggleGroupItem>
          <ToggleGroupItem
            value="custom"
            className={cn(
              'rounded-none px-6 py-2 text-sm font-medium border-l border-border cursor-pointer',
              currentPreset === 'custom'
                ? 'bg-primary text-primary-foreground shadow-md font-semibold'
                : 'text-muted-foreground hover:bg-background/60',
            )}
          >
            Custom
          </ToggleGroupItem>
        </ToggleGroup>

        {/* Custom Date Inputs (only shown when Custom is active, to the right) */}
        {currentPreset === 'custom' && (
          <>
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium whitespace-nowrap">
                From:
              </span>
              <DatePicker
                value={customFromDate || undefined}
                onSelect={handleCustomFromDateChange}
                placeholder="Select date"
                className="w-[180px]"
                disabled={customToDate ? { after: customToDate } : undefined}
              />
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium whitespace-nowrap">To:</span>
              <DatePicker
                value={customToDate || undefined}
                onSelect={handleCustomToDateChange}
                placeholder="Select date"
                className="w-[180px]"
                disabled={
                  customFromDate ? { before: customFromDate } : undefined
                }
              />
            </div>
          </>
        )}
      </div>

      {/* Inline Preview Text */}
      <p className="text-sm text-muted-foreground">
        Showing results for: {formatDateRangePreview(value, currentPreset)}
      </p>
    </div>
  );
};

export default DateRangePicker;
